<template>
  <div class="content">
    <div class="sd-pathway-dashboard-wrap">
      <card class="card no-border-card" no-body>
        <div class="sdp-dashboard">

          <!-- Header ───────────────────────────────────────────── -->
          <header class="sdp-head">
            <div class="sdp-head__text">
              <h1 class="sdp-title">{{ isLearnerView ? 'My Pathways' : 'Pathways' }}</h1>
              <p class="sdp-sub">
                {{ isLearnerView
                    ? 'A pathway is a learning program: a set of courses taken in order. Open one to see its modules.'
                    : 'Build and publish training pathways for excavators, locators and homeowners.' }}
              </p>
            </div>
            <div v-if="canCreate" class="sdp-head__cta">
              <button class="sdp-btn sdp-btn--ghost" @click="openTemplate">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 17H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v4"></path><rect x="7" y="11" width="14" height="10" rx="2"></rect></svg>
                Create from template
              </button>
              <button class="sdp-btn sdp-btn--primary" @click="$router.push('/safedig/pathways/new')">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                Create pathway
              </button>
            </div>
          </header>

          <!-- Stat cards (staff only) — each one is also the status filter ── -->
          <section v-if="!isLearnerView" class="sdp-stats" role="group" aria-label="Filter pathways by status">
            <button
              v-for="card in statCards"
              :key="card.status"
              type="button"
              class="sdp-stat"
              :class="{ 'is-active': filterStatus === card.status }"
              :aria-pressed="filterStatus === card.status ? 'true' : 'false'"
              @click="setStatusFilter(card.status)">
              <div :class="['sdp-stat__icon', 'sdp-stat__icon--' + card.tone]" v-html="card.icon"></div>
              <div class="sdp-stat__body">
                <div class="sdp-stat__value">{{ stats[card.key] }}</div>
                <div class="sdp-stat__label">{{ card.label }}</div>
                <div class="sdp-stat__hint">{{ card.hint }}</div>
              </div>
            </button>
          </section>

          <!-- Filters bar (staff) ──────────────────────────────── -->
          <section v-if="!isLearnerView" class="sdp-filters">
            <div class="sdp-search">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sdp-search__icon"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              <input v-model="search" type="text" placeholder="Search pathways..." class="sdp-search__input" aria-label="Search pathways" />
            </div>
            <div v-if="!isLearnerView" class="sdp-select">
              <select v-model="filterStatus" aria-label="Filter by status">
                <option value="">All statuses</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sdp-select__chev"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </div>
            <div v-if="!isLearnerView" class="sdp-select">
              <select v-model="filterClassification" aria-label="Filter by pathway type">
                <option value="">All types</option>
                <option v-for="c in classifications" :key="c.value" :value="c.value">{{ c.label }}</option>
                <option value="__none">No type set</option>
              </select>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sdp-select__chev"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </div>
            <div class="sdp-select">
              <select v-model="sortKey" aria-label="Sort pathways">
                <option value="updated_desc">Sort: Updated (newest)</option>
                <option value="updated_asc">Sort: Updated (oldest)</option>
                <option value="title_asc">Sort: Title (A–Z)</option>
                <option value="title_desc">Sort: Title (Z–A)</option>
              </select>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sdp-select__chev"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </div>
            <div class="sdp-view-toggle">
              <button :class="['sdp-view-btn', { 'is-active': view === 'list' }]" @click="view = 'list'" aria-label="List view">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
              </button>
              <button :class="['sdp-view-btn', { 'is-active': view === 'grid' }]" @click="view = 'grid'" aria-label="Grid view">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
              </button>
            </div>
          </section>

          <template v-if="!isLearnerView">
          <!-- Loading / empty ──────────────────────────────────── -->
          <div v-if="loading" class="sdp-loading">
            <span class="sdp-spinner" aria-label="Loading pathways"></span>
          </div>
          <div v-else-if="filtered.length === 0" class="sdp-empty">
            <p v-if="pathways.length === 0 && isLearnerView">No pathways have been assigned to you yet.</p>
            <p v-else-if="pathways.length === 0">No pathways yet.</p>
            <p v-else>No pathways match your filters.</p>
          </div>

          <!-- Table view ───────────────────────────────────────── -->
          <div v-else-if="view === 'list'" class="sdp-table-wrap">
            <table class="sdp-table">
              <thead>
                <tr>
                  <th class="sdp-th-title" @click="toggleSort('title')">
                    Title
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sdp-th-sort"><polyline points="6 9 12 4 18 9"></polyline><polyline points="6 15 12 20 18 15"></polyline></svg>
                  </th>
                  <th>Description</th>
                  <th>Modules</th>
                  <th>Est. Time</th>
                  <th>{{ isLearnerView ? 'Progress' : 'Status' }}</th>
                  <th @click="toggleSort('updated')">
                    Updated
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sdp-th-sort"><polyline points="6 9 12 4 18 9"></polyline><polyline points="6 15 12 20 18 15"></polyline></svg>
                  </th>
                  <th class="sdp-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, idx) in paginated" :key="'p-' + p.id" @click="openPathway(p.id)">
                  <td class="sdp-cell-title">
                    <div class="sdp-title-cell">
                      <div :class="['sdp-icon-tile', iconClass(p, idx)]" v-html="iconSvg(p, idx)"></div>
                      <span class="sdp-row-title">{{ p.title }}</span>
                      <span v-if="!isLearnerView" :class="['sdp-type', classificationClass(p.classification)]" :title="classificationTitle(p.classification)">
                        {{ classificationLabel(p.classification) }}
                      </span>
                    </div>
                  </td>
                  <td class="sdp-cell-desc">{{ rowDescription(p) }}</td>
                  <td class="sdp-cell-modules">{{ moduleCount(p) }} modules</td>
                  <td class="sdp-cell-time">{{ estDuration(p) }} min</td>
                  <td>
                    <span v-if="isLearnerView" :class="['sdp-pill', progressPillClass(p)]">{{ progressLabel(p) }}</span>
                    <span v-else :class="['sdp-pill', pillClass(p.status)]">{{ pillLabel(p.status) }}</span>
                  </td>
                  <td class="sdp-cell-updated">{{ formatDate(p.updated_at) }}</td>
                  <td class="sdp-cell-actions" @click.stop>
                    <button class="sdp-icon-btn" @click="openPathway(p.id)" aria-label="Open pathway">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                    </button>
                    <button v-if="canCreate" class="sdp-icon-btn" @click="toggleMenu(p.id, $event)" aria-label="More actions">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></svg>
                    </button>
                    <div v-if="openMenuId === p.id" class="sdp-menu" :style="menuStyle" @click.stop>
                      <button @click="editPathway(p.id)">Edit</button>
                      <button @click="duplicatePathway(p.id)">Duplicate</button>
                      <button class="sdp-menu__danger" @click="archivePathway(p)">
                        {{ p.status === 'archived' ? 'Restore as draft' : 'Archive' }}
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Grid view ────────────────────────────────────────── -->
          <div v-else class="sdp-grid">
            <article v-for="(p, idx) in paginated" :key="'g-' + p.id" class="sdp-card" @click="openPathway(p.id)" tabindex="0" role="button" @keyup.enter="openPathway(p.id)">
              <div :class="['sdp-card__icon', iconClass(p, idx)]" v-html="iconSvg(p, idx)"></div>
              <div class="sdp-card__body">
                <span v-if="isLearnerView" :class="['sdp-pill', progressPillClass(p)]">{{ progressLabel(p) }}</span>
                <span v-else :class="['sdp-pill', pillClass(p.status)]">{{ pillLabel(p.status) }}</span>
                <h3 class="sdp-card__title">{{ p.title }}</h3>
                <p class="sdp-card__desc">{{ rowDescription(p) }}</p>
                <div class="sdp-card__meta">
                  <span v-if="!isLearnerView" :class="['sdp-type', classificationClass(p.classification)]">{{ classificationLabel(p.classification) }}</span>
                  <span v-if="!isLearnerView" class="sdp-card__sep">·</span>
                  <span>{{ moduleCount(p) }} modules</span>
                  <span class="sdp-card__sep">·</span>
                  <span>{{ estDuration(p) }} min</span>
                </div>
                <div v-if="isLearnerView && moduleCount(p) > 0" class="sdp-card__bar">
                  <span class="sdp-card__bar-fill" :style="{ width: completionPct(p) + '%' }"></span>
                </div>
                <div class="sdp-card__foot">
                  <span>Updated {{ formatDate(p.updated_at) }}</span>
                </div>
              </div>
            </article>
          </div>

          <!-- Pagination ───────────────────────────────────────── -->
          <footer v-if="filtered.length > 0" class="sdp-paging">
            <span class="sdp-paging__info">
              Showing {{ rangeStart }} to {{ rangeEnd }} of {{ filtered.length }} pathways
            </span>
            <div class="sdp-paging__pager">
              <button class="sdp-icon-btn" :disabled="page === 1" @click="page = Math.max(1, page - 1)" aria-label="Previous page">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
              </button>
              <button v-for="n in totalPages" :key="n" :class="['sdp-page-btn', { 'is-active': n === page }]" @click="page = n">{{ n }}</button>
              <button class="sdp-icon-btn" :disabled="page === totalPages" @click="page = Math.min(totalPages, page + 1)" aria-label="Next page">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
              </button>
            </div>
          </footer>
          </template>

        </div>
      </card>

      <!-- ───── Template gallery modal ──────────────────────────── -->
      <transition name="sdp-modal-fade">
        <div
          v-if="templateGalleryOpen"
          class="sdp-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sdp-tpl-title"
          @click.self="closeTemplateGallery">
          <div class="sdp-modal__panel">
            <header class="sdp-modal__head">
              <div>
                <h2 id="sdp-tpl-title" class="sdp-modal__title">Choose a template</h2>
                <p class="sdp-modal__sub">Start from a pre-built pathway. You can change everything afterwards.</p>
              </div>
              <button class="sdp-modal__close" type="button" aria-label="Close" @click="closeTemplateGallery">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </header>

            <div class="sdp-tpl-grid">
              <button
                v-for="tpl in templates"
                :key="tpl.id"
                type="button"
                class="sdp-tpl-card"
                @click="pickTemplate(tpl)">
                <div :class="['sdp-tpl-card__icon', 'sdp-tpl-card__icon--' + tpl.accent]">
                  <svg v-if="tpl.icon === 'plus'"          viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                  <svg v-else-if="tpl.icon === 'book'"     viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>
                  <svg v-else-if="tpl.icon === 'award'"    viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="7"></circle><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline></svg>
                  <svg v-else-if="tpl.icon === 'users'"    viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                  <svg v-else-if="tpl.icon === 'sparkles'" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l2 4 4 2-4 2-2 4-2-4-4-2 4-2z"></path><path d="M19 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1z"></path></svg>
                  <svg v-else-if="tpl.icon === 'compass'"  viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"></polygon></svg>
                </div>
                <div class="sdp-tpl-card__body">
                  <div class="sdp-tpl-card__title">{{ tpl.name }}</div>
                  <div class="sdp-tpl-card__desc">{{ tpl.description }}</div>
                  <div class="sdp-tpl-card__meta">
                    <span v-if="tpl.classification" class="sdp-tpl-card__chip">{{ classificationLabel(tpl.classification) }}</span>
                    <span class="sdp-tpl-card__hint">{{ tpl.moduleHint }}</span>
                    <span class="sdp-tpl-card__dot" aria-hidden="true">·</span>
                    <span class="sdp-tpl-card__hint">{{ tpl.duration }}</span>
                  </div>
                </div>
                <div class="sdp-tpl-card__arrow" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                </div>
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import {
  SAFEDIG_PATHWAY_TEMPLATES,
  SAFEDIG_PATHWAY_CLASSIFICATIONS,
  safeDigClassificationLabel,
} from '@/utils/safeDigPathwayTemplates';

const ICON_PRESETS = [
  { cls: 'sdp-icon-tile--blue',  svg: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>' },
  { cls: 'sdp-icon-tile--green', svg: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"></path><path d="M6 12v5c3 3 9 3 12 0v-5"></path></svg>' },
  { cls: 'sdp-icon-tile--amber', svg: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>' },
  { cls: 'sdp-icon-tile--teal',  svg: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>' },
  { cls: 'sdp-icon-tile--navy',  svg: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>' },
  { cls: 'sdp-icon-tile--gray',  svg: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="21 8 21 21 3 21 3 8"></polyline><rect x="1" y="3" width="22" height="5"></rect><line x1="10" y1="12" x2="14" y2="12"></line></svg>' },
];

/**
 * The four stat cards. `status` is the value written to filterStatus when the
 * card is clicked ('' clears the filter); `key` picks the number out of stats.
 */
const STAT_CARDS = [
  {
    status: '', key: 'total', label: 'Total pathways', hint: 'Across all statuses', tone: 'blue',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>',
  },
  {
    status: 'published', key: 'published', label: 'Published', hint: 'Visible to learners', tone: 'green',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>',
  },
  {
    status: 'draft', key: 'drafts', label: 'Drafts', hint: 'Not yet published', tone: 'amber',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"></path></svg>',
  },
  {
    status: 'archived', key: 'archived', label: 'Archived', hint: 'Not visible', tone: 'gray',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="21 8 21 21 3 21 3 8"></polyline><rect x="1" y="3" width="22" height="5"></rect><line x1="10" y1="12" x2="14" y2="12"></line></svg>',
  },
];

/** Roles allowed to author pathways — mirrors AUTHOR_ROLES on the API. */
const AUTHOR_ROLES = ['super-admin', 'sub-admin'];
/**
 * Roles that see the management view. Everyone else — company admins and
 * managers included, since those are customer accounts — gets My Pathways:
 * only what an admin assigned to them. Mirrors STAFF_ROLES on the API.
 */
const STAFF_ROLES = ['super-admin', 'sub-admin'];

export default {
  name: 'PathwayDashboard',
  data() {
    return {
      loading: true,
      pathways: [],
      search: '',
      filterStatus: '',
      filterClassification: '',
      sortKey: 'updated_desc',
      view: 'list',
      page: 1,
      perPage: 10,
      openMenuId: null,
      // The row menu is position: fixed so the table's overflow-x doesn't clip
      // it; these are the coordinates worked out from the button it hangs off.
      menuStyle: {},
      role: (typeof localStorage !== 'undefined') ? localStorage.getItem('hot-user') : '',
      templateGalleryOpen: false,
      templates: SAFEDIG_PATHWAY_TEMPLATES,
      classifications: SAFEDIG_PATHWAY_CLASSIFICATIONS,
      statCards: STAT_CARDS,
    };
  },
  computed: {
    isStaff() { return STAFF_ROLES.indexOf(this.role) !== -1; },
    isLearnerView() { return !this.isStaff; },
    canCreate() { return AUTHOR_ROLES.indexOf(this.role) !== -1; },
    stats() {
      const total = this.pathways.length;
      let published = 0, drafts = 0, archived = 0;
      this.pathways.forEach(p => {
        if (p.status === 'published') published += 1;
        else if (p.status === 'archived') archived += 1;
        else drafts += 1;
      });
      return { total, published, drafts, archived };
    },
    filtered() {
      const q = this.search.trim().toLowerCase();
      const list = this.pathways.filter(p => {
        if (q
          && !(p.title || '').toLowerCase().includes(q)
          && !(p.subtitle || '').toLowerCase().includes(q)) return false;
        if (this.filterStatus && p.status !== this.filterStatus) return false;
        if (this.filterClassification === '__none') {
          if (p.classification) return false;
        } else if (this.filterClassification && p.classification !== this.filterClassification) {
          return false;
        }
        return true;
      });
      return list.slice().sort((a, b) => {
        switch (this.sortKey) {
          case 'updated_asc':  return new Date(a.updated_at || 0) - new Date(b.updated_at || 0);
          case 'title_asc':    return (a.title || '').localeCompare(b.title || '');
          case 'title_desc':   return (b.title || '').localeCompare(a.title || '');
          case 'updated_desc':
          default:             return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
        }
      });
    },
    totalPages() { return Math.max(1, Math.ceil(this.filtered.length / this.perPage)); },
    paginated() {
      const start = (this.page - 1) * this.perPage;
      return this.filtered.slice(start, start + this.perPage);
    },
    rangeStart() { return this.filtered.length === 0 ? 0 : (this.page - 1) * this.perPage + 1; },
    rangeEnd() { return Math.min(this.filtered.length, this.page * this.perPage); },
  },
  watch: {
    filtered() { if (this.page > this.totalPages) this.page = 1; },
    // A status change is a new result set, so always start it from page 1 —
    // covers both the cards and the select, since they share this state.
    filterStatus() { this.page = 1; },
    filterClassification() { this.page = 1; },
  },
  created() {
    // Learners are served by the My Learning component, which fetches its own data.
    // Learners read their pathways on My Courses & Pathways; this page is
    // the staff management view.
    if (this.isLearnerView) {
      this.$router.replace('/employee_courses');
      return;
    }
    this.fetchPathways();
    document.addEventListener('click', this.closeMenus);
    // A fixed-position menu would otherwise hang in place while the page or
    // the table scrolls out from under it. Capture phase so it also catches
    // scrolling inside .sdp-table-wrap, which doesn't bubble.
    window.addEventListener('scroll', this.closeMenus, true);
    window.addEventListener('resize', this.closeMenus);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeMenus);
    document.removeEventListener('keydown', this.handleTemplateKeydown);
    window.removeEventListener('scroll', this.closeMenus, true);
    window.removeEventListener('resize', this.closeMenus);
  },
  methods: {
    fetchPathways() {
      this.loading = true;
      this.$http.get('safedig/pathways')
        .then(resp => { this.pathways = Array.isArray(resp.data) ? resp.data : []; })
        .catch(() => { this.pathways = []; })
        .finally(() => { this.loading = false; });
    },
    openPathway(id) {
      if (this.canCreate) { this.$router.push('/safedig/pathways/' + id + '/edit'); return; }
      this.$router.push('/safedig/pathways/' + id);
    },
    editPathway(id) {
      this.openMenuId = null;
      this.$router.push('/safedig/pathways/' + id + '/edit');
    },
    /**
     * Opens the create form pre-filled from this pathway. Nothing is written
     * until the user saves — same shape as Courses → Duplicate, which sends
     * the source id to /create_course and lets the form fetch it.
     */
    duplicatePathway(id) {
      this.openMenuId = null;
      this.$router.push({ path: '/safedig/pathways/new', query: { duplicate: String(id) } });
    },
    archivePathway(p) {
      this.openMenuId = null;
      const nextStatus = p.status === 'archived' ? 'draft' : 'archived';
      this.$http.put('safedig/pathways/' + p.id, { status: nextStatus })
        .then(() => {
          this.$set(p, 'status', nextStatus);
          this.flash(nextStatus === 'archived' ? 'Pathway archived' : 'Pathway restored as a draft', 'success');
        })
        .catch(err => {
          const msg = (err && err.response && err.response.data && err.response.data.message)
            || 'Could not update this pathway.';
          this.flash(msg, 'error');
        });
    },
    flash(msg, kind) {
      if (this.$toast && this.$toast[kind]) this.$toast[kind](msg, { timeout: 4000 });
    },
    openTemplate() {
      this.templateGalleryOpen = true;
      this.$nextTick(() => document.addEventListener('keydown', this.handleTemplateKeydown));
    },
    closeTemplateGallery() {
      this.templateGalleryOpen = false;
      document.removeEventListener('keydown', this.handleTemplateKeydown);
    },
    handleTemplateKeydown(e) {
      if (e.key === 'Escape') this.closeTemplateGallery();
    },
    pickTemplate(tpl) {
      this.closeTemplateGallery();
      if (!tpl || tpl.id === 'blank') {
        this.$router.push('/safedig/pathways/new');
        return;
      }
      this.$router.push({ path: '/safedig/pathways/new', query: { template: tpl.id } });
    },
    classificationLabel(c) { return safeDigClassificationLabel(c) || 'No type'; },
    classificationClass(c) {
      return {
        'sdp-type--homeowner':    c === 'homeowner',
        'sdp-type--professional': c === 'professional',
        'sdp-type--both':         c === 'both',
        'sdp-type--none':         !c,
      };
    },
    classificationTitle(c) {
      if (!c) return 'No pathway type set — never assigned automatically at sign-up';
      if (c === 'both') return 'Assigned automatically to every new user';
      return 'Assigned automatically to new ' + c + 's, and to users who chose both';
    },
    setStatusFilter(status) { this.filterStatus = status; },
    /**
     * Anchor the menu to the button in viewport coordinates. It has to be
     * position: fixed — the table wrapper scrolls on overflow-x, and an
     * absolutely positioned menu is clipped by it and drifts with the scroll.
     */
    toggleMenu(id, event) {
      if (this.openMenuId === id) {
        this.openMenuId = null;
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const MENU_WIDTH = 170;
      const MENU_HEIGHT = 88;
      const GAP = 6;

      // Flip above the button when there isn't room underneath.
      const opensUpward = (window.innerHeight - rect.bottom) < (MENU_HEIGHT + GAP);

      this.menuStyle = {
        width: MENU_WIDTH + 'px',
        left: Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8)) + 'px',
        top: (opensUpward ? rect.top - MENU_HEIGHT - GAP : rect.bottom + GAP) + 'px',
      };
      this.openMenuId = id;
    },
    closeMenus() { this.openMenuId = null; },
    toggleSort(field) {
      if (field === 'title') this.sortKey = this.sortKey === 'title_asc' ? 'title_desc' : 'title_asc';
      if (field === 'updated') this.sortKey = this.sortKey === 'updated_desc' ? 'updated_asc' : 'updated_desc';
    },
    moduleCount(p) {
      if (typeof p.module_count === 'number') return p.module_count;
      return Array.isArray(p.modules) ? p.modules.length : 0;
    },
    completedCount(p) {
      if (!Array.isArray(p.modules)) return 0;
      return p.modules.filter(m => m.module_status === 'completed').length;
    },
    completionPct(p) {
      const total = this.moduleCount(p);
      if (!total) return 0;
      return Math.round((this.completedCount(p) / total) * 100);
    },
    estDuration(p) { return Math.max(5, this.moduleCount(p) * 12); },
    rowDescription(p) {
      const text = p.subtitle || p.description || '';
      if (text.length <= 80) return text;
      return text.slice(0, 78).trimEnd() + '…';
    },
    formatDate(s) {
      if (!s) return '—';
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    pillLabel(status) {
      if (status === 'published') return 'Published';
      if (status === 'archived')  return 'Archived';
      return 'Draft';
    },
    pillClass(status) {
      return {
        'sdp-pill--green': status === 'published',
        'sdp-pill--amber': !status || status === 'draft',
        'sdp-pill--gray':  status === 'archived',
      };
    },
    progressLabel(p) {
      if (p.pathway_status === 'completed') return 'Completed';
      if (p.pathway_status === 'not_enrolled' || !p.pathway_status) return 'Not enrolled';
      const total = this.moduleCount(p);
      if (!total) return 'Enrolled';
      return this.completedCount(p) + ' of ' + total + ' done';
    },
    progressPillClass(p) {
      return {
        'sdp-pill--green': p.pathway_status === 'completed',
        'sdp-pill--blue':  p.pathway_status === 'in_progress' || p.pathway_status === 'enrolled',
        'sdp-pill--gray':  !p.pathway_status || p.pathway_status === 'not_enrolled',
      };
    },
    iconClass(p, idx) {
      if (p.status === 'archived') return ICON_PRESETS[5].cls;
      return ICON_PRESETS[(p.id || idx) % 5].cls;
    },
    iconSvg(p, idx) {
      const preset = p.status === 'archived' ? ICON_PRESETS[5] : ICON_PRESETS[(p.id || idx) % 5];
      return preset.svg;
    },
  },
};
</script>

<style scoped>
.sd-pathway-dashboard-wrap { width: 100%; }
.sd-pathway-dashboard-wrap >>> .card.no-border-card {
  border: 0;
  box-shadow: none;
  background: transparent;
}

.sdp-dashboard {
  --sd-accent: #3e7799;
  --sd-accent-dark: #2d5a73;
  --sd-accent-soft: rgba(62, 119, 153, 0.14);
  --sd-navy: #1e3a5f;
  --sd-text: #0f172a;
  --sd-muted: #475569;
  --sd-border: #e2e8f0;
  --sd-radius: 12px;
  --sd-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 6px 16px rgba(15, 23, 42, 0.05);

  padding: 1.75rem 2rem 2.5rem;
  color: var(--sd-text);
  background: #f8fafc;
  min-height: 100%;
}

/* Header ──────────────────────────────────────────────────── */
.sdp-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.25rem;
  margin-bottom: 1.75rem;
  flex-wrap: wrap;
}
.sdp-title {
  font-size: 1.85rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--sd-navy);
  margin: 0 0 0.35rem;
  line-height: 1.15;
}
.sdp-sub {
  color: var(--sd-muted);
  font-size: 0.9375rem;
  margin: 0;
}
.sdp-head__cta { display: flex; gap: 0.6rem; flex-wrap: wrap; }

.sdp-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.05rem;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--sd-border);
  background: #fff;
  color: var(--sd-text);
  line-height: 1;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.04);
}
.sdp-btn:hover { border-color: var(--sd-accent); color: var(--sd-accent); }
.sdp-btn--primary {
  background: var(--sd-accent);
  color: #fff;
  border-color: var(--sd-accent);
}
.sdp-btn--primary:hover {
  background: var(--sd-accent-dark);
  border-color: var(--sd-accent-dark);
  color: #fff;
}

/* Stat cards ──────────────────────────────────────────────── */
.sdp-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.sdp-stat {
  background: #fff;
  border: 1px solid var(--sd-border);
  border-radius: 14px;
  padding: 1.1rem 1.2rem;
  display: flex;
  gap: 0.95rem;
  align-items: center;
  box-shadow: var(--sd-shadow);
  /* It's a <button> now: strip the UA styling and make the whole card clickable. */
  width: 100%;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}
.sdp-stat:hover { border-color: var(--sd-accent); }
.sdp-stat:focus-visible { outline: 2px solid var(--sd-accent); outline-offset: 2px; }
.sdp-stat.is-active {
  border-color: var(--sd-accent);
  background: #f4f8fb;
  box-shadow: 0 0 0 3px var(--sd-accent-soft), var(--sd-shadow);
}
.sdp-stat.is-active .sdp-stat__label { color: var(--sd-accent); }
.sdp-stat__icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.sdp-stat__icon--blue  { background: var(--sd-accent-soft); color: var(--sd-accent); }
.sdp-stat__icon--green { background: #dcfce7; color: #15803d; }
.sdp-stat__icon--amber { background: #fef3c7; color: #b45309; }
.sdp-stat__icon--gray  { background: #f1f5f9; color: var(--sd-muted); }
.sdp-stat__value {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--sd-navy);
}
.sdp-stat__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--sd-text);
  margin-top: 0.15rem;
}
.sdp-stat__hint {
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 0.1rem;
}

/* Filters bar ─────────────────────────────────────────────── */
.sdp-filters {
  display: flex;
  gap: 0.6rem;
  margin-bottom: 0.9rem;
  flex-wrap: wrap;
  align-items: center;
}
.sdp-search { flex: 1; min-width: 220px; position: relative; }
.sdp-search__icon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--sd-muted);
  pointer-events: none;
}
.sdp-search__input {
  width: 100%;
  padding: 0.6rem 0.85rem 0.6rem 2.25rem;
  border: 1px solid var(--sd-border);
  border-radius: 10px;
  background: #fff;
  font-size: 0.875rem;
  color: var(--sd-text);
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.sdp-search__input:focus {
  border-color: var(--sd-accent);
  box-shadow: 0 0 0 3px var(--sd-accent-soft);
}
.sdp-select { position: relative; }
.sdp-select select {
  appearance: none;
  -webkit-appearance: none;
  padding: 0.6rem 2rem 0.6rem 0.85rem;
  border: 1px solid var(--sd-border);
  border-radius: 10px;
  background: #fff;
  font-size: 0.875rem;
  color: var(--sd-text);
  cursor: pointer;
  outline: none;
}
.sdp-select select:focus {
  border-color: var(--sd-accent);
  box-shadow: 0 0 0 3px var(--sd-accent-soft);
}
.sdp-select__chev {
  position: absolute;
  right: 0.7rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--sd-muted);
  pointer-events: none;
}
.sdp-view-toggle {
  display: inline-flex;
  border: 1px solid var(--sd-border);
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}
.sdp-view-btn {
  border: 0;
  background: transparent;
  padding: 0.55rem 0.7rem;
  color: var(--sd-muted);
  cursor: pointer;
  line-height: 1;
}
.sdp-view-btn.is-active { background: var(--sd-accent-soft); color: var(--sd-accent); }

/* Loading / empty ─────────────────────────────────────────── */
.sdp-loading, .sdp-empty {
  padding: 3rem 1rem;
  text-align: center;
  color: var(--sd-muted);
}
.sdp-spinner {
  display: inline-block;
  width: 28px;
  height: 28px;
  border: 3px solid var(--sd-border);
  border-top-color: var(--sd-accent);
  border-radius: 50%;
  animation: sdp-spin 0.8s linear infinite;
}
@keyframes sdp-spin { to { transform: rotate(360deg); } }

/* Table ───────────────────────────────────────────────────── */
.sdp-table-wrap {
  background: #fff;
  border: 1px solid var(--sd-border);
  border-radius: var(--sd-radius);
  box-shadow: var(--sd-shadow);
  overflow-x: auto;
}
.sdp-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
.sdp-table thead th {
  text-align: left;
  padding: 0.85rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--sd-muted);
  border-bottom: 1px solid var(--sd-border);
  background: #f8fafc;
  white-space: nowrap;
}
.sdp-th-title, .sdp-table thead th[class] { cursor: pointer; }
.sdp-th-sort { opacity: 0.5; margin-left: 0.25rem; vertical-align: middle; }
.sdp-table tbody tr {
  border-bottom: 1px solid var(--sd-border);
  cursor: pointer;
  transition: background 0.12s ease;
}
.sdp-table tbody tr:last-child { border-bottom: 0; }
.sdp-table tbody tr:hover { background: #f8fafc; }
.sdp-table td { padding: 0.85rem 1rem; vertical-align: middle; }
.sdp-title-cell { display: flex; align-items: center; gap: 0.7rem; }
.sdp-row-title { font-weight: 600; color: var(--sd-navy); }
/* Pathway type (who it's auto-assigned to at sign-up) */
.sdp-type {
  display: inline-block;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
  margin-left: 0.5rem;
}
.sdp-type--homeowner    { background: #ccfbf1; color: #0f766e; }
.sdp-type--professional { background: var(--sd-accent-soft); color: var(--sd-accent); }
.sdp-type--both         { background: #e0e7ff; color: var(--sd-navy); }
.sdp-type--none         { background: #fef3c7; color: #b45309; }
.sdp-card__meta .sdp-type { margin-left: 0; }
.sdp-cell-desc, .sdp-cell-modules, .sdp-cell-time, .sdp-cell-updated { color: var(--sd-muted); }
.sdp-cell-actions { white-space: nowrap; position: relative; }

.sdp-icon-tile, .sdp-card__icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.sdp-icon-tile--blue,  .sdp-card__icon.sdp-icon-tile--blue  { background: var(--sd-accent-soft); color: var(--sd-accent); }
.sdp-icon-tile--green, .sdp-card__icon.sdp-icon-tile--green { background: #dcfce7; color: #15803d; }
.sdp-icon-tile--amber, .sdp-card__icon.sdp-icon-tile--amber { background: #fef3c7; color: #b45309; }
.sdp-icon-tile--teal,  .sdp-card__icon.sdp-icon-tile--teal  { background: #ccfbf1; color: #0f766e; }
.sdp-icon-tile--navy,  .sdp-card__icon.sdp-icon-tile--navy  { background: #e0e7ff; color: var(--sd-navy); }
.sdp-icon-tile--gray,  .sdp-card__icon.sdp-icon-tile--gray  { background: #f1f5f9; color: var(--sd-muted); }

/* Pills ───────────────────────────────────────────────────── */
.sdp-pill {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}
.sdp-pill--green { background: #dcfce7; color: #15803d; }
.sdp-pill--amber { background: #fef3c7; color: #b45309; }
.sdp-pill--gray  { background: #f1f5f9; color: var(--sd-muted); }
.sdp-pill--blue  { background: var(--sd-accent-soft); color: var(--sd-accent); }

/* Icon buttons + row menu ─────────────────────────────────── */
.sdp-icon-btn {
  border: 1px solid var(--sd-border);
  background: #fff;
  border-radius: 8px;
  padding: 0.35rem 0.45rem;
  color: var(--sd-muted);
  cursor: pointer;
  line-height: 1;
  margin-right: 0.25rem;
  transition: border-color 0.15s ease, color 0.15s ease;
}
.sdp-icon-btn:hover:not(:disabled) { border-color: var(--sd-accent); color: var(--sd-accent); }
.sdp-icon-btn:disabled { opacity: 0.45; cursor: not-allowed; }
/* Fixed, not absolute: .sdp-table-wrap scrolls on overflow-x, which clips an
   absolutely positioned menu and drags it along with the table. Coordinates
   come from toggleMenu(). */
.sdp-menu {
  position: fixed;
  z-index: 1200;
  background: #fff;
  border: 1px solid var(--sd-border);
  border-radius: 10px;
  box-shadow: var(--sd-shadow);
  min-width: 160px;
  padding: 0.3rem;
}
.sdp-menu button {
  display: block;
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  padding: 0.5rem 0.65rem;
  font-size: 0.85rem;
  color: var(--sd-text);
  border-radius: 7px;
  cursor: pointer;
}
.sdp-menu button:hover { background: #f1f5f9; }
.sdp-menu__danger { color: #b91c1c; }

/* Grid ────────────────────────────────────────────────────── */
.sdp-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}
.sdp-card {
  background: #fff;
  border: 1px solid var(--sd-border);
  border-radius: var(--sd-radius);
  box-shadow: var(--sd-shadow);
  padding: 1.15rem;
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.15s ease;
}
.sdp-card:hover { border-color: var(--sd-accent); transform: translateY(-2px); }
.sdp-card:focus { outline: 2px solid var(--sd-accent); outline-offset: 2px; }
.sdp-card__icon { margin-bottom: 0.85rem; }
.sdp-card__title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--sd-navy);
  margin: 0.5rem 0 0.35rem;
}
.sdp-card__desc {
  font-size: 0.85rem;
  color: var(--sd-muted);
  margin: 0 0 0.7rem;
  min-height: 2.4em;
}
.sdp-card__meta { font-size: 0.8rem; color: var(--sd-muted); }
.sdp-card__sep { margin: 0 0.35rem; }
.sdp-card__bar {
  margin-top: 0.7rem;
  height: 6px;
  border-radius: 999px;
  background: #f1f5f9;
  overflow: hidden;
}
.sdp-card__bar-fill {
  display: block;
  height: 100%;
  background: var(--sd-accent);
  transition: width 0.3s ease;
}
.sdp-card__foot {
  margin-top: 0.8rem;
  padding-top: 0.65rem;
  border-top: 1px solid var(--sd-border);
  font-size: 0.75rem;
  color: #94a3b8;
}

/* Pagination ──────────────────────────────────────────────── */
.sdp-paging {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.1rem;
  flex-wrap: wrap;
  gap: 0.75rem;
}
.sdp-paging__info { font-size: 0.8rem; color: var(--sd-muted); }
.sdp-paging__pager { display: flex; align-items: center; gap: 0.25rem; }
.sdp-page-btn {
  min-width: 30px;
  border: 1px solid var(--sd-border);
  background: #fff;
  border-radius: 8px;
  padding: 0.3rem 0.5rem;
  font-size: 0.8rem;
  color: var(--sd-text);
  cursor: pointer;
}
.sdp-page-btn.is-active {
  background: var(--sd-accent);
  border-color: var(--sd-accent);
  color: #fff;
}

/* Template gallery modal ──────────────────────────────────── */
.sdp-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1.5rem;
}
.sdp-modal__panel {
  background: #fff;
  border-radius: 16px;
  max-width: 720px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  padding: 1.5rem;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
}
.sdp-modal__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
}
.sdp-modal__title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--sd-navy);
  margin: 0 0 0.25rem;
}
.sdp-modal__sub { font-size: 0.875rem; color: var(--sd-muted); margin: 0; }
.sdp-modal__close {
  border: 0;
  background: transparent;
  color: var(--sd-muted);
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;
}
.sdp-tpl-grid { display: grid; gap: 0.65rem; }
.sdp-tpl-card {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  width: 100%;
  text-align: left;
  border: 1px solid var(--sd-border);
  border-radius: var(--sd-radius);
  background: #fff;
  padding: 0.9rem 1rem;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.sdp-tpl-card:hover { border-color: var(--sd-accent); background: #f8fafc; }
.sdp-tpl-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.sdp-tpl-card__icon--blue  { background: var(--sd-accent-soft); color: var(--sd-accent); }
.sdp-tpl-card__icon--green { background: #dcfce7; color: #15803d; }
.sdp-tpl-card__icon--amber { background: #fef3c7; color: #b45309; }
.sdp-tpl-card__icon--teal  { background: #ccfbf1; color: #0f766e; }
.sdp-tpl-card__icon--navy  { background: #e0e7ff; color: var(--sd-navy); }
.sdp-tpl-card__icon--gray  { background: #f1f5f9; color: var(--sd-muted); }
.sdp-tpl-card__body { flex: 1; min-width: 0; }
.sdp-tpl-card__title { font-weight: 600; color: var(--sd-navy); font-size: 0.9375rem; }
.sdp-tpl-card__desc { font-size: 0.8125rem; color: var(--sd-muted); margin-top: 0.15rem; }
.sdp-tpl-card__meta {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.4rem;
  font-size: 0.75rem;
  color: #94a3b8;
  flex-wrap: wrap;
}
.sdp-tpl-card__chip {
  background: var(--sd-accent-soft);
  color: var(--sd-accent);
  border-radius: 999px;
  padding: 0.1rem 0.5rem;
  font-weight: 600;
}
.sdp-tpl-card__arrow { color: var(--sd-muted); flex-shrink: 0; }
.sdp-modal-fade-enter-active, .sdp-modal-fade-leave-active { transition: opacity 0.18s ease; }
.sdp-modal-fade-enter, .sdp-modal-fade-leave-to { opacity: 0; }

@media (max-width: 960px) {
  .sdp-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .sdp-dashboard { padding: 1.25rem 1rem 2rem; }
}
@media (max-width: 520px) {
  .sdp-stats { grid-template-columns: 1fr; }
}
</style>
